import React, { useId } from "react";
import PropTypes from "prop-types";
import { getShape } from "../catalog/shapes";
import { ICONS } from "../catalog/icons";
import { ellipsePath, roundedPolygonPath, roundedRectPath, scalePoints } from "../catalog/geometry";
import { radiusOf, withAlpha } from "../defaults";

const FONT_FALLBACK = "inherit";

// Text sizes are stored as px on the 1100px reference canvas, where the canvas
// font base is 14px (common_tools.changeFontBase); em keeps them proportional
// at every canvas size, editor zoom and Visor fullscreen alike.
const em = (px) => `${(Number(px) || 0) / 14}em`;

function dashArray(dash, sw) {
  if (dash === "dashed") return `${sw * 3} ${sw * 2}`;
  if (dash === "dotted") return `0 ${sw * 2}`;
  return undefined;
}

function Marker({ id, kind, color }) {
  if (kind === "arrow") {
    return (
      <marker
        id={id}
        viewBox="0 0 10 10"
        refX="8"
        refY="5"
        markerWidth="4"
        markerHeight="4"
        markerUnits="strokeWidth"
        orient="auto-start-reverse"
      >
        <path d="M0 0L10 5L0 10Z" fill={color} />
      </marker>
    );
  }
  if (kind === "circle") {
    return (
      <marker
        id={id}
        viewBox="0 0 10 10"
        refX="5"
        refY="5"
        markerWidth="3"
        markerHeight="3"
        markerUnits="strokeWidth"
        orient="auto"
      >
        <circle cx="5" cy="5" r="5" fill={color} />
      </marker>
    );
  }
  return null;
}

Marker.propTypes = { id: PropTypes.string, kind: PropTypes.string, color: PropTypes.string };

// Draws one VectorShape from a resolved state (see defaults.resolveState) at
// a given pixel size. Used by the canvas, the Visor and the panel thumbnails.
// `pxScale` shrinks a numeric corner radius for previews
// drawn smaller than the real box, so a thumbnail keeps the box's proportions.
export default function ShapeRenderer({ state, width, height, thumbnail, pxScale = 1 }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const shape = getShape(state.shapeId) || getShape("rect");
  const w = Math.max(1, width || 0);
  const h = Math.max(1, height || 0);
  const { fill, stroke, shadow, text } = state;

  const sw = shape.caps.stroke ? Math.max(0, Number(stroke.width) || 0) : 0;
  const strokePaint = sw > 0 ? withAlpha(stroke.color, stroke.transparency) : "none";
  const isLine = shape.kind === "line";
  const hasCaps = isLine && (stroke.startCap !== "none" || stroke.endCap !== "none");
  const pad = sw / 2 + (hasCaps ? sw * 2 : 0);
  const x = pad;
  const y = pad;
  const iw = Math.max(1, w - pad * 2);
  const ih = Math.max(1, h - pad * 2);
  const ownRadius = state.radius == null ? shape.radius : state.radius;
  const r = shape.caps.radius
    ? radiusOf(shape, state, iw, ih) * (typeof ownRadius === "number" ? pxScale : 1)
    : 0;

  const gradientId = `vs-grad-${uid}`;
  const startId = `vs-start-${uid}`;
  const endId = `vs-end-${uid}`;

  let fillPaint = "none";
  if (!isLine && shape.caps.fill) {
    if (fill.type === "solid") fillPaint = withAlpha(fill.color, fill.transparency);
    if (fill.type === "gradient") fillPaint = `url(#${gradientId})`;
  }

  const defs = [];
  if (fillPaint === `url(#${gradientId})`) {
    defs.push(
      <linearGradient
        key="grad"
        id={gradientId}
        x1="0"
        y1="0.5"
        x2="1"
        y2="0.5"
        gradientTransform={`rotate(${(Number(fill.angle) || 0) - 90} 0.5 0.5)`}
      >
        <stop offset="0" stopColor={withAlpha(fill.color, fill.transparency)} />
        <stop offset="1" stopColor={withAlpha(fill.color2, fill.transparency)} />
      </linearGradient>
    );
  }
  if (isLine && sw > 0) {
    if (stroke.startCap !== "none") {
      defs.push(<Marker key="s" id={startId} kind={stroke.startCap} color={strokePaint} />);
    }
    if (stroke.endCap !== "none") {
      defs.push(<Marker key="e" id={endId} kind={stroke.endCap} color={strokePaint} />);
    }
  }

  const strokeProps = {
    stroke: strokePaint,
    strokeWidth: sw || undefined,
    strokeDasharray: sw ? dashArray(stroke.dash, sw) : undefined,
    strokeLinecap: isLine ? (stroke.dash === "dotted" ? "round" : stroke.lineCap) : undefined,
    strokeLinejoin: "round",
  };

  let body;
  switch (shape.kind) {
    case "rect":
      body = (
        <path
          data-shape-body
          d={roundedRectPath(x, y, iw, ih, r)}
          fill={fillPaint}
          {...strokeProps}
        />
      );
      break;
    case "ellipse":
      body = (
        <path data-shape-body d={ellipsePath(x, y, iw, ih)} fill={fillPaint} {...strokeProps} />
      );
      break;
    case "poly":
      body = (
        <path
          data-shape-body
          d={roundedPolygonPath(scalePoints(shape.points, x, y, iw, ih), r)}
          fill={fillPaint}
          {...strokeProps}
        />
      );
      break;
    case "custom":
      body = (
        <path data-shape-body d={shape.build(x, y, iw, ih, r)} fill={fillPaint} {...strokeProps} />
      );
      break;
    case "path":
      body = (
        <path
          data-shape-body
          d={shape.d}
          transform={`translate(${x} ${y}) scale(${iw / 100} ${ih / 100})`}
          vectorEffect="non-scaling-stroke"
          fill={fillPaint}
          {...strokeProps}
        />
      );
      break;
    case "line":
      body = (
        <path
          data-shape-body
          d={shape.build(x, y, iw, ih, sw)}
          fill="none"
          {...strokeProps}
          markerStart={stroke.startCap !== "none" && sw ? `url(#${startId})` : undefined}
          markerEnd={stroke.endCap !== "none" && sw ? `url(#${endId})` : undefined}
        />
      );
      break;
    case "icon": {
      const Icon = ICONS[shape.icon];
      const s = Math.min(iw, ih);
      body = Icon ? (
        <Icon
          data-shape-body
          size={s}
          x={x + (iw - s) / 2}
          y={y + (ih - s) / 2}
          fill={fillPaint}
          stroke={strokePaint}
          strokeWidth={sw ? (sw * 512) / s : 0}
          style={{ overflow: "visible" }}
        />
      ) : null;
      break;
    }
    default:
      body = null;
  }

  const showText = !thumbnail && shape.caps.text && text.value;
  const filter =
    shadow.enabled && !thumbnail
      ? `drop-shadow(${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color})`
      : undefined;

  return (
    <div className="vector-shape" style={{ position: "relative", width: "100%", height: "100%" }}>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ display: "block", overflow: "visible", filter }}
        aria-hidden="true"
      >
        {defs.length ? <defs>{defs}</defs> : null}
        {body}
      </svg>
      {showText ? (
        <div
          className="vector-shape__text"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems:
              text.valign === "top"
                ? "flex-start"
                : text.valign === "bottom"
                  ? "flex-end"
                  : "center",
            justifyContent:
              text.align === "left" ? "flex-start" : text.align === "right" ? "flex-end" : "center",
            padding: em(text.padding),
            boxSizing: "border-box",
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              width: "100%",
              textAlign: text.align,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: text.color,
              fontSize: em(Number(text.size) || 18),
              lineHeight: 1.25,
              fontFamily: text.font ? `'${text.font}', sans-serif` : FONT_FALLBACK,
              fontWeight: text.bold ? 700 : 400,
              fontStyle: text.italic ? "italic" : "normal",
            }}
          >
            {text.value}
          </span>
        </div>
      ) : null}
    </div>
  );
}

ShapeRenderer.propTypes = {
  state: PropTypes.object.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  thumbnail: PropTypes.bool,
  pxScale: PropTypes.number,
};
